import React, {
  useState,
  useCallback,
  useEffect,
  Suspense,
  useMemo,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spacer, LoadingSpinner, useTheme } from '@tw/tw-component-library';
import { Heading } from '@tw/tw-component-library/common-wt';
import {
  Table,
  buildFilterDefinitionsFromData,
  TABLE_SERVER_FILTER_SECTIONS,
  type TableFilterDefinition,
} from '@tw/tw-component-library/containers-wt';
import { getAllWorkflows } from '../../../apis/workflow/get/getAllWorkflows';
import { useWorkflowContext } from '../../../contexts/WorkflowContext';
import { UseAuth } from '../../../contexts/AuthContext';
import { RequestData } from '../../../types';
import { workflowsRegistry } from '../../../utils/workflowRegistry';
import { Container } from '../../pages/workflows/styles';
import {
  canViewWorkflow,
  isInvolvedWithWorkflowType,
} from '../../../utils/permissions';
import { useUnifiedFilters } from '../../../hooks/useUnifiedFilters';
import { GetAllWorkflowsParams } from '../../../apis/workflow/get/getAllWorkflows';
import { getAssignedWorkflows } from '../../../apis/workflow/get/getAssignedWorkflows';
import { getOfficeForWorkflowType } from '../../../utils/workflowRegistry';
import { useFormContext } from '../../../contexts/useFormContext';
import { getSpoofUserHeader } from '../../container/forms/components/FormSection/utils';
import { PermissionOverwriteBanner } from '../PermissionOverwriteBanner';
import { shouldBypassWorkflowBackend } from '../../../utils/workflowTestingBypass';

const ITEMS_PER_PAGE = 20; // Match the API page size

const CLIENT_FILTER_FIELDS = [
  { id: 'status' as const, label: 'Status' },
  { id: 'createdByName' as const, label: 'Requested By' },
];

export const CentralizedRequestsPage: React.FC = () => {
  const { theme } = useTheme();
  const { workflowKey } = useParams<{ workflowKey: string }>();
  const { getAccessToken, currentUser, spoofedUser } = UseAuth();
  const [allWorkFlows, setAllWorkFlows] = useState<RequestData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentSortColumn, setCurrentSortColumn] = useState<
    keyof RequestData | undefined
  >('id');

  const [currentSortOrder, setCurrentSortOrder] = useState<'asc' | 'desc'>(
    'desc',
  );

  const [searchDraft, setSearchDraft] = useState('');

  const {
    filterMode,
    setFilterMode,
    clientFilters,
    serverFilters,
    updateClientFilter,
    updateServerFilter,
    updateSort,
    clearFilters,
    getApiParams,
  } = useUnifiedFilters();

  const { refreshTrigger } = useWorkflowContext();

  const { updateFormData } = useFormContext();

  const navigate = useNavigate();

  const workflow = useMemo(() => {
    return workflowKey ? workflowsRegistry[workflowKey] : undefined;
  }, [workflowKey]);

  // Fetch requests regardless; if workflow undefined we'll skip render later
  const fetchRequests = useCallback(
    async (page: number = 1, append: boolean = false) => {
      if (!workflow || !currentUser) return;

      if (page === 1) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      try {
        // Testing: registry `permissions: true` — no getAllWorkflows / assignment APIs.
        if (shouldBypassWorkflowBackend(workflow.workflowType)) {
          setAllWorkFlows([]);
          setHasMore(false);
          setCurrentPage(1);
          return;
        }

        const userForPermissionCheck = currentUser;
        const hasTypeAccess = canViewWorkflow(
          userForPermissionCheck,
          workflow.workflowType,
        );
        let restrictToAssigned = false;

        if (!hasTypeAccess) {
          const assignedResponse = await getAssignedWorkflows(
            getAccessToken,
            getSpoofUserHeader(spoofedUser),
          );
          const assignedWorkflows = assignedResponse.success
            ? assignedResponse.data?.workflows ?? []
            : [];

          if (
            !isInvolvedWithWorkflowType(
              assignedWorkflows,
              workflow.workflowType,
            )
          ) {
            setAllWorkFlows([]);
            navigate('/403');
            return;
          }

          restrictToAssigned = true;
        }

        // Only use API filters in server mode. Involved-only users see
        // workflows they are assigned to, not the full type list.
        const apiFilters: GetAllWorkflowsParams =
          filterMode === 'server'
            ? {
                ...getApiParams(),
                workflowType: workflow.workflowType,
                ...(restrictToAssigned ? { currentlyAssigned: true } : {}),
              }
            : {
                workflowType: workflow.workflowType,
                ...(restrictToAssigned ? { currentlyAssigned: true } : {}),
              };

        const requests = await getAllWorkflows(
          getAccessToken,
          getSpoofUserHeader(spoofedUser),
          page,
          ITEMS_PER_PAGE,
          apiFilters,
        );

        if (requests.success) {
          const apiItems = (
            requests.data as unknown as {
              items: RequestData[];
              totalPages: number;
              totalCount: number;
            }
          ).items;

          if (append) {
            setAllWorkFlows((prev: RequestData[]) => {
              const existingIds = new Set(prev.map((item) => item.id));
              const uniqueNewItems = apiItems.filter(
                (item) => !existingIds.has(item.id),
              );
              const itemsWithUniqueId = uniqueNewItems.map((item, index) => ({
                ...item,
                _uniqueId: `${item.id}-${Date.now()}-${index}`,
              }));
              return [...prev, ...itemsWithUniqueId];
            });
          } else {
            const itemsWithUniqueId = apiItems.map((item, index) => ({
              ...item,
              _uniqueId: `${item.id}-${Date.now()}-${index}`,
            }));
            setAllWorkFlows(itemsWithUniqueId);
          }

          const totalPages =
            (requests.data as unknown as { totalPages: number }).totalPages ||
            1;
          const hasMoreData =
            apiItems.length === ITEMS_PER_PAGE && page < totalPages;
          setHasMore(hasMoreData);
          setCurrentPage(page);
        } else {
          console.error('🔴 Failed to fetch requests');
          setAllWorkFlows([]);
        }
      } catch (error) {
        console.error('🔴 Error fetching requests:', error);
        setAllWorkFlows([]);
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },

    [
      getAccessToken,
      workflow,
      currentUser,
      spoofedUser,
      getApiParams,
      filterMode,
      navigate,
    ],
  );

  const fetchMoreWorkFlows = useCallback(() => {
    if (isLoadingMore || !hasMore) return;

    fetchRequests(currentPage + 1, true);
  }, [isLoadingMore, hasMore, currentPage, fetchRequests]);

  const handleWindowScroll = useCallback(() => {
    if (isLoadingMore || !hasMore) return;

    const scrollPosition = window.innerHeight + window.scrollY;

    const threshold = 100;

    if (document.body.offsetHeight - scrollPosition < threshold) {
      fetchMoreWorkFlows();
    }
  }, [fetchMoreWorkFlows, isLoadingMore, hasMore]);

  useEffect(() => {
    if (workflow) {
      fetchRequests(1, false);
    }
  }, [fetchRequests, refreshTrigger, workflow]);

  // Set office in form state when workflow changes

  useEffect(() => {
    if (workflow?.workflowType) {
      const office = getOfficeForWorkflowType(workflow.workflowType);

      updateFormData({ office });
    }
  }, [workflow?.workflowType, updateFormData]);

  // Refetch data when server filters change

  useEffect(() => {
    if (filterMode === 'server') {
      fetchRequests(1, false);
    }
  }, [serverFilters, filterMode, fetchRequests]);

  // Sync sort state from URL parameters

  useEffect(() => {
    if (serverFilters.sortColumn) {
      setCurrentSortColumn(serverFilters.sortColumn as keyof RequestData);
    }

    if (serverFilters.sortDescending !== undefined) {
      setCurrentSortOrder(serverFilters.sortDescending ? 'desc' : 'asc');
    }
  }, [serverFilters.sortColumn, serverFilters.sortDescending]);

  useEffect(() => {
    setSearchDraft(serverFilters.searchTerm || '');
  }, [serverFilters.searchTerm]);

  useEffect(() => {
    window.addEventListener('scroll', handleWindowScroll);

    return () => window.removeEventListener('scroll', handleWindowScroll);
  }, [handleWindowScroll]);

  // Apply client-side filtering when in client mode

  const filteredData = useMemo(() => {
    if (filterMode === 'server') {
      return allWorkFlows;
    }

    if (!allWorkFlows || Object.keys(clientFilters).length === 0) {
      return allWorkFlows;
    }

    return allWorkFlows.filter((item) => {
      return Object.entries(clientFilters).every(([key, values]) => {
        if (!values || values.length === 0) return true;

        const itemValue = item[key as keyof RequestData] as string;

        return values.includes(itemValue);
      });
    });
  }, [allWorkFlows, clientFilters, filterMode]);

  const tableFilters = useMemo((): TableFilterDefinition[] => {
    if (filterMode === 'server') {
      return TABLE_SERVER_FILTER_SECTIONS;
    }

    return buildFilterDefinitionsFromData(
      (allWorkFlows || []) as unknown as Record<string, unknown>[],

      CLIENT_FILTER_FIELDS,
    );
  }, [allWorkFlows, filterMode]);

  const selectedByFilter = useMemo((): Record<string, string[]> => {
    if (filterMode === 'server') {
      const selected: Record<string, string[]> = {};

      if (serverFilters.currentlyAssigned === true) {
        selected.currentlyAssigned = ['true'];
      }

      if (serverFilters.sortDescending === true) {
        selected.sortDescending = ['true'];
      } else if (serverFilters.sortDescending === false) {
        selected.sortDescending = ['false'];
      }

      return selected;
    }

    return {
      status: clientFilters.status || [],

      createdByName: clientFilters.createdByName || [],
    };
  }, [clientFilters, filterMode, serverFilters]);

  const handleFilterChange = useCallback(
    (filterId: string, values: string[]) => {
      if (filterMode === 'client') {
        if (filterId === 'status' || filterId === 'createdByName') {
          updateClientFilter(filterId, values);
        }

        return;
      }

      if (filterId === 'currentlyAssigned') {
        updateServerFilter(
          'currentlyAssigned',

          values.includes('true') ? true : undefined,
        );

        return;
      }

      if (filterId === 'sortDescending') {
        const selectedValue = values.at(-1);

        if (selectedValue === 'true') {
          updateServerFilter('sortDescending', true);
        } else if (selectedValue === 'false') {
          updateServerFilter('sortDescending', false);
        } else {
          updateServerFilter('sortDescending', undefined);
        }
      }
    },

    [filterMode, updateClientFilter, updateServerFilter],
  );

  const handleSearchSubmit = useCallback(() => {
    if (filterMode === 'server') {
      updateServerFilter('searchTerm', searchDraft || undefined);
    }
  }, [filterMode, searchDraft, updateServerFilter]);

  const handleFiltersReset = useCallback(() => {
    clearFilters();

    setSearchDraft('');
  }, [clearFilters]);

  // Custom sort handler for API-based sorting

  const handleSort = useCallback(
    (column: string, order: 'asc' | 'desc') => {
      if (filterMode === 'server') {
        updateSort(column, order);
        setCurrentSortColumn(column as keyof RequestData);
        setCurrentSortOrder(order);
        setCurrentPage(1);
        setAllWorkFlows([]);
      }
    },
    [filterMode, updateSort],
  );

  if (!workflow) {
    return (
      <Container>
        <Spacer />

        <Heading level={2}>Workflow not found</Heading>

        <p>The workflow "{workflowKey}" could not be found.</p>
      </Container>
    );
  }

  if (!currentUser) {
    return (
      <Container>
        <Spacer />
        <LoadingSpinner variant="fullPage" />
      </Container>
    );
  }

  const handleRowClick = (row: Record<string, unknown>) => {
    if (row.id) {
      navigate(`/request/${workflowKey}/${row.id}`);
    }
  };

  const columns = workflow.columns || [
    { key: 'id', header: 'ID', sortable: true },
    { key: 'name', header: 'Name', sortable: true },
    { key: 'status', header: 'Current State', sortable: true },
    { key: 'createdByName', header: 'Requested By', sortable: true },
    {
      key: 'createdAt',
      header: 'Created',
      type: 'date' as const,
      sortable: true,
    },
  ];

  const customCellRenderer = (
    cellValue: unknown,
    columnKey: string,
  ): React.ReactNode => {
    if (columnKey === 'currentSectionAssignments' || columnKey === 'authoriser') {
      if (!cellValue || !Array.isArray(cellValue) || cellValue.length === 0) {
        return (
          <span style={{ color: theme.colors.text.secondary }}>
            No assignments
          </span>
        );
      }

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {cellValue.map((assignment: unknown, idx: number) => (
            <span key={idx}>
              {(assignment as { displayName: string }).displayName}
            </span>
          ))}
        </div>
      );
    }

    if (columnKey === 'createdAt' && cellValue) {
      return new Date(String(cellValue)).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    }

    return undefined;
  };

  return (
    <Container>
      <Spacer />
      {shouldBypassWorkflowBackend(workflow.workflowType) && (
        <PermissionOverwriteBanner />
      )}
      <Heading level={2}>{workflow.name}</Heading>
      <Spacer />
      <Suspense fallback={<LoadingSpinner variant="fullPage" />}>
        <Table
          data={(filteredData || []) as unknown as Record<string, unknown>[]}
          columns={columns}
          customCellRenderer={customCellRenderer}
          onRowClick={handleRowClick}
          enableSort={filterMode === 'client'}
          enableSearch={filterMode === 'server'}
          searchPlaceholder="Search workflows by name..."
          searchQuery={searchDraft}
          onSearchChange={setSearchDraft}
          onSearchSubmit={handleSearchSubmit}
          filters={tableFilters}
          selectedByFilter={selectedByFilter}
          onFilterChange={handleFilterChange}
          onFiltersApply={() => undefined}
          onFiltersReset={handleFiltersReset}
          showFilterModeToggle
          filterMode={filterMode}
          onFilterModeChange={setFilterMode}
          isLoading={isLoading}
          showPagination={false}
          {...(filterMode === 'server' ? { onSort: handleSort } : {})}
          customSort={filterMode === 'server' ? () => 0 : undefined}
          defaultSortColumn={
            filterMode === 'client'
              ? 'id'
              : (currentSortColumn as string | undefined)
          }
          defaultSortOrder={filterMode === 'client' ? 'desc' : currentSortOrder}
        />
      </Suspense>

      {isLoadingMore && (
        <div
          style={{
            textAlign: 'center',
            padding: '1rem',
            marginTop: '1rem',
          }}
        >
          <LoadingSpinner size="small" />
          <div>Loading more requests...</div>
        </div>
      )}

      {!hasMore &&
        filteredData &&
        filteredData.length > 0 &&
        !isLoadingMore && (
          <div
            style={{
              textAlign: 'center',
              padding: '1rem',
              color: theme.colors.text.secondary,
            }}
          >
            No more requests to load
          </div>
        )}
    </Container>
  );
};
